/**
 * OAuthCallback — landing page for directory-integration OAuth redirects.
 *
 * Mounted via `main.jsx` when the page loads at
 * `/integrations/oauth-callback` (the redirect URI we register with the
 * provider; see `lib/oauthCallback.js`). The hash router never
 * touches it. Requests the pending integration context from the exact
 * opener window, calls the appropriate
 * exchange endpoint, returns the result to the exact opener window so
 * the originating dashboard tab can update its form, and (if running
 * in a popup opened by `window.open`) closes itself.
 */
import { useEffect, useState } from "react";
import { completeEntraIdminConsent, getDirectoryAuthUrl } from "../lib/identityClient.js";
import { completeCopilotAdminConsent, getCopilotAuthUrl } from "../lib/copilotClient.js";
import { completeMicrosoftFoundryAdminConsent, getMicrosoftFoundryAuthUrl } from "../lib/microsoftFoundryClient.js";
import { denialOrRawMessage } from "../lib/denialReason.js";
import { AUTH_REQUIRED } from "../lib/featureFlags.js";
import {
    sessionBindingFromJwt,
    setTokens,
} from "../lib/authTokenStore.js";
import {
    DIRECTORY_OAUTH_RESULT,
    DIRECTORY_OAUTH_SESSION_REQUEST,
    DIRECTORY_OAUTH_SESSION_RESPONSE,
    isDirectoryOauthPending,
    readDirectoryOauthRequestId,
    sameAuthBinding,
} from "../lib/directoryOauthHandshake.js";

/**
 * Ensure this freshly-loaded callback tab holds an access token before it
 * calls the directory-finalize endpoints.
 *
 * `main.jsx` mounts this page WITHOUT running `bootstrapAuth()` — the
 * redirect URI is a flat path and we don't want to drag the admin through a
 * full WorkOS round-trip just to finalize a Graph consent. But WorkOS tokens
 * live in a per-tab in-memory store (`authTokenStore.js`), so this popup
 * loads with an empty store. Without an opener-bound handoff the first `/v1`
 * call throws `AuthMissingError` and the integration never connects.
 *
 * Request the session directly from the exact opener window. The random
 * window-name nonce correlates the request without putting tenant data in
 * origin-wide storage, and the response JWT must match the opener's captured
 * issuer, subject, session, and effective tenant binding.
 */
export async function ensureCallbackSession(requestId, timeoutMs = 1_500) {
    if (!requestId || !window.opener || window.opener === window) return null;

    return new Promise((resolve) => {
        let settled = false;
        const finish = (result) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener("message", onMessage);
            resolve(result);
        };
        const onMessage = (event) => {
            if (
                event?.origin !== window.location.origin
                || event?.source !== window.opener
                || event?.data?.type !== DIRECTORY_OAUTH_SESSION_RESPONSE
                || event?.data?.requestId !== requestId
            ) return;
            const message = event.data;
            const pending = message.pending;
            if (!isDirectoryOauthPending(pending, requestId)) {
                finish(null);
                return;
            }
            if (AUTH_REQUIRED) {
                if (!sameAuthBinding(sessionBindingFromJwt(message.access), pending.binding)) {
                    finish(null);
                    return;
                }
                setTokens({ access: message.access, refresh: message.refresh, user: message.user });
            }
            finish(pending);
        };
        const timer = window.setTimeout(() => finish(null), timeoutMs);
        window.addEventListener("message", onMessage);
        window.opener.postMessage({
            type: DIRECTORY_OAUTH_SESSION_REQUEST,
            requestId,
        }, window.location.origin);
    });
}

function sendResult(pending, msg) {
    if (!window.opener || window.opener === window) return;
    window.opener.postMessage({
        ...msg,
        type: DIRECTORY_OAUTH_RESULT,
        requestId: pending.requestId,
        authBinding: pending.binding,
    }, window.location.origin);
}

function isPopupTab() {
    return !!window.opener && window.opener !== window;
}

const CONSENT_COMPLETERS = {
    ENTRA_ID: completeEntraIdminConsent,
    COPILOT: completeCopilotAdminConsent,
    MICROSOFT_FOUNDRY: completeMicrosoftFoundryAdminConsent,
};

const SIGN_IN_CONTINUERS = {
    ENTRA_ID: getDirectoryAuthUrl,
    COPILOT: getCopilotAuthUrl,
    MICROSOFT_FOUNDRY: getMicrosoftFoundryAuthUrl,
};

export async function finalizeConsent(pending, params) {
    const complete = CONSENT_COMPLETERS[pending.provider];
    if (!complete) {
        throw new Error(
            "This connection doesn't finish through a browser redirect. Close this tab and start the connection again from the dashboard.",
        );
    }
    const code = params.get("code");
    if (code) {
        const { authUrl } = await SIGN_IN_CONTINUERS[pending.provider]({
            integrationId: pending.integrationId,
            redirectUri: pending.redirectUri,
            code,
            state: params.get("state") ?? "",
        });
        if (!authUrl) throw new Error("Origin did not return the Microsoft consent page.");
        return { next: authUrl };
    }
    const adminConsent = params.get("admin_consent") === "True"
        || params.get("admin_consent") === "true";
    if (!adminConsent) throw new Error("Admin consent was not granted.");
    await complete({
        integrationId: pending.integrationId,
        state: params.get("state") ?? "",
        adminConsent,
        tenant: params.get("tenant") ?? "",
    });
    return {};
}

// An RPC error's message wraps the server's reason in the request line, HTTP
// status and JSON body; the admin needs only the reason.
export function consentFailureMessage(err) {
    return (err && denialOrRawMessage(err)) || "Failed to finish connecting.";
}

export default function OAuthCallback() {
    const [status, setStatus] = useState({ phase: "working", message: "Finalizing connection…" });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const requestId = readDirectoryOauthRequestId();
            if (!requestId) {
                if (!cancelled) setStatus({
                    phase: "error",
                    message: "No pending connection found. Close this tab and start the connection again from the dashboard.",
                });
                return;
            }
            const pending = await ensureCallbackSession(requestId);
            if (cancelled) return;
            if (!pending) {
                if (!cancelled) setStatus({
                    phase: "error",
                    message: "Couldn't verify the dashboard tab that started this connection. Close this tab and start the connection again from the dashboard.",
                });
                return;
            }
            const params = new URLSearchParams(window.location.search);
            const finish = (result, message, phase) => {
                sendResult(pending, result);
                if (cancelled) return;
                setStatus({ phase, message });
                if (isPopupTab()) {
                    window.setTimeout(() => window.close(), 600);
                    if (phase === "success") {
                        window.setTimeout(() => window.location.assign("/"), 1400);
                    }
                } else if (phase === "success") {
                    window.setTimeout(() => window.location.assign("/"), 800);
                }
            };

            const providerError = params.get("error");
            if (providerError) {
                const description = params.get("error_description") ?? providerError;
                const message = `Provider returned an error: ${description}`;
                finish({ integrationId: pending.integrationId, status: "error", message }, message, "error");
                return;
            }

            try {
                const { next } = await finalizeConsent(pending, params);
                if (next) {
                    if (!cancelled) setStatus({ phase: "working", message: "Signed in. Opening Microsoft consent..." });
                    window.location.assign(next);
                    return;
                }
                finish(
                    { integrationId: pending.integrationId, status: "success" },
                    "Connected. Returning to the dashboard...", "success",
                );
            } catch (err) {
                const message = consentFailureMessage(err);
                finish({ integrationId: pending.integrationId, status: "error", message }, message, "error");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const color =
        status.phase === "success" ? "text-[color:var(--green)]" :
        status.phase === "error"   ? "text-red-400" :
        "text-[color:var(--muted)]";

    return (
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg)]">
            <div className="max-w-md w-full border border-[color:var(--line)] bg-[color:var(--bg)] p-[20px]">
                <h1 className="m-0 text-[13px] font-semibold uppercase tracking-[0.08em] text-[color:var(--ink)] mb-[12px]">
                    Microsoft connection
                </h1>
                <p className={`m-0 text-[12px] ${color}`}>{status.message}</p>
                {status.phase === "error" ? (
                    <div className="mt-[16px] flex gap-[6px]">
                        <button
                            type="button"
                            onClick={() => window.close()}
                            className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                        >
                            Close tab
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.assign("/")}
                            className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                        >
                            Return to dashboard
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
